import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Checkbox,
  IconButton,
  MenuItem,
  Box,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Autocomplete,
  useTheme,
  alpha,
} from "@mui/material";
import {
  useDeleteStudentMutation,
  useGetallEnumQuery,
  useGetallStudentQuery,
  useLazyGetallStudentQuery,
  useGetallGradeQuery,
  useGetallLocationQuery,
  useGetTeamQuery,
  useGetallSubjectQuery,
} from "../../../Api/attoDeskApi";
import {
  ITeam,
  IGrade,
  IEnumType,
  IStudent,
  IStudentListPayload,
  IPaginationMeta,
  StudentSortBy,
  SortDirection,
} from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import SearchableSelect from "../../../Components/SearchableSelect";
import StudentAvatar from "../../../Components/StudentAvatar";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { formatPhoneNumberForDisplay } from "../../../Components/Validation/validationUtils";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { useSelector } from "react-redux";
import { RootState } from "../../../Store";
import LoadingPage from "../../../Components/LoadingDialog/LoadingPage";
import Pagination from "../../../Components/Pagination";
import ReplaceImage from "../../../Images/student.png";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import { hasPaymentDue } from "../../../Core/constants/studentStatus";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SchoolIcon from "@mui/icons-material/School";
import GroupIcon from "@mui/icons-material/Group";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import useUser from "../../../Core/hook/useUser";
// ── Shared Step Up design tokens ────────────────────────────────────
// The Student List is aligned to the same B13 palette + surface
// treatment used by the Courses List, so navigating Students →
// Courses reads as the same product. All colour, radius, shadow, and
// spacing values below flow from these tokens — no hardcoded hexes.
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx, primaryBtnSx, secondaryBtnSx } from "../../Course/shared/moduleSx";

// Shared filter-field sx object — 44 px pill, 10 px radius, brand
// hover + focus states. Same visual family as the Courses filter
// toolbar so the two lists feel like siblings.
const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44,
    borderRadius: "10px",
    bgcolor: "#FFFFFF",
    transition: "box-shadow 160ms ease, border-color 160ms ease",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": {
      borderColor: B13.primary,
      borderWidth: "1px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(31,111,235,0.15)",
    },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink },
  "& .MuiInputLabel-root": { fontWeight: 500, color: B13Ink.ink3 },
  "& .MuiInputLabel-root.Mui-focused": { color: B13.primary },
} as const;

type SortOption = {
  value: string;
  label: string;
  sort_by: StudentSortBy;
  sort_direction: SortDirection;
};

// Mirrors the sort whitelist in studentsController::applyStudentSort().
const SORT_OPTIONS: SortOption[] = [
  { value: "name:asc", label: "Name A–Z", sort_by: "name", sort_direction: "asc" },
  { value: "name:desc", label: "Name Z–A", sort_by: "name", sort_direction: "desc" },
  { value: "student_id:asc", label: "Student ID: Low to High", sort_by: "student_id", sort_direction: "asc" },
  { value: "student_id:desc", label: "Student ID: High to Low", sort_by: "student_id", sort_direction: "desc" },
  { value: "created_at:desc", label: "Newest First", sort_by: "created_at", sort_direction: "desc" },
  { value: "created_at:asc", label: "Oldest First", sort_by: "created_at", sort_direction: "asc" },
];

const DEFAULT_SORT = "name:asc";

const cap = (s: string | null | undefined) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const fullName = (row: IStudent) =>
  [cap(row.first_name), cap(row.middle_name), cap(row.last_name)]
    .filter(Boolean)
    .join(" ");

const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "active":
      return { bg: "#ecfdf5", text: "#065f46" };
    case "drop":
    case "dropped":
    case "inactive":
      return { bg: "#fef2f2", text: "#991b1b" };
    case "on hold":
    case "hold":
      return { bg: "#fffbeb", text: "#92400e" };
    default:
      return { bg: "#f3f4f6", text: "#374151" };
  }
};

// Canonical display label. Stored values include both "Drop" and (legacy)
// "Dropped"; both render as "Drop" to match the dropdown option.
const displayStatus = (status?: string | null): string => {
  const s = (status || "").toLowerCase();
  if (s === "drop" || s === "dropped") return "Drop";
  return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "Unknown";
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canCreate, canEdit } = useUser();
  const isMobileRes = useIsMobile();
  const { showErrorMessage, showMessage } = useNotifier();
  const { companyLogo, logowidthPrint } = useSelector(
    (state: RootState) => state.company
  );

  // Hydrate from URL query so refresh + browser back/forward preserve
  // the current page, search, sort, and active filters. See sync
  // effect below — the URL is the source of truth on mount, then
  // React state stays authoritative and we push updates back to the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = Math.max(0, parseInt(searchParams.get("page") || "1", 10) - 1);
  const urlSearch = searchParams.get("search") || "";
  const urlSort = searchParams.get("sort") || DEFAULT_SORT;
  const urlGrade = searchParams.get("grade_id") || "";
  const urlLocation = searchParams.get("location_id") || "";
  // Status default: `null` (param absent) means first visit — apply the
  // Active default. `""` (param present but empty, e.g. after Clear all)
  // means the user explicitly cleared it — respect that on refresh.
  const rawUrlStatus = searchParams.get("status");
  const urlStatus = rawUrlStatus === null ? "active" : rawUrlStatus;
  const urlStaff = searchParams.get("staff_id") || "";
  // Subject filter rides in the URL as a CSV of ids so refresh / shareable
  // links preserve the multi-selection. Non-numeric entries are dropped
  // defensively (never let a hand-edited URL explode the query payload).
  const urlSubjects = useMemo<number[]>(() => {
    const raw = searchParams.get("subject_id") || "";
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search & Pagination
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [currentSearchQuery, setCurrentSearchQuery] = useState(urlSearch);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(urlPage);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterGrade, setFilterGrade] = useState<string>(urlGrade);
  const [filterLocation, setFilterLocation] = useState<string>(urlLocation);
  const [filterStatus, setFilterStatus] = useState<string>(urlStatus);
  const [filterStaff, setFilterStaff] = useState<string>(urlStaff);
  const [filterSubjects, setFilterSubjects] = useState<number[]>(urlSubjects);

  // Sort
  const [sortValue, setSortValue] = useState<string>(urlSort);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);

  // Row action menu
  const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [rowMenuStudent, setRowMenuStudent] = useState<IStudent | null>(null);

  // Delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<IStudent | null>(null);

  // Bulk action dialogs
  const [bulkMessageOpen, setBulkMessageOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  const activeSort = useMemo(
    () => SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0],
    [sortValue]
  );

  // Every filter + the sort go to the API, so pagination totals stay correct.
  // Empty string means "no filter" and is dropped rather than sent as "".
  const listFilters: IStudentListPayload = useMemo(
    () => ({
      searchText: currentSearchQuery,
      id: "",
      grade_id: filterGrade || undefined,
      location_id: filterLocation || undefined,
      status: filterStatus || undefined,
      staff_id: filterStaff || undefined,
      // Only serialise subject_id when at least one is selected — keeps
      // the payload identical to pre-feature requests for admins who
      // never touch the new filter (preserves RTKQ cache keys).
      subject_id: filterSubjects.length > 0 ? filterSubjects : undefined,
      sort_by: activeSort.sort_by,
      sort_direction: activeSort.sort_direction,
    }),
    [currentSearchQuery, filterGrade, filterLocation, filterStatus, filterStaff, filterSubjects, activeSort]
  );

  // Data queries
  const { data, isLoading, isFetching, isError } = useGetallStudentQuery({
    ...listFilters,
    page: page + 1,
    perPage: rowsPerPage,
  });
  const [fetchAllStudents] = useLazyGetallStudentQuery();
  const { data: enumData, isLoading: enumLoading } = useGetallEnumQuery({
    searchText: "",
    enum_type: "",
  });
  const { data: gradeData, isLoading: gradeLoading } = useGetallGradeQuery({
    searchText: "",
  });
  const { data: locationData } = useGetallLocationQuery({
    searchText: "",
  });
  const { data: staffData } = useGetTeamQuery({ searchText: "", id: "" });
  const { data: subjectData } = useGetallSubjectQuery({ searchText: "" });

  const [deleteStudent] = useDeleteStudentMutation();

  // Memos
  const enumList = useMemo(() => (enumData?.data as IEnumType[]) || [], [enumData]);
  const gradeList: IGrade[] = useMemo(
    () => (Array.isArray(gradeData?.data) ? (gradeData!.data as IGrade[]) : []),
    [gradeData]
  );
  const locationList: any[] = useMemo(
    () => (Array.isArray(locationData?.data) ? (locationData!.data as any[]) : []),
    [locationData]
  );
  const staffList: ITeam[] = useMemo(
    () => (Array.isArray(staffData?.data) ? (staffData!.data as ITeam[]) : []),
    [staffData]
  );
  type SubjectOption = { id: number; subject_name: string };
  const subjectList: SubjectOption[] = useMemo(() => {
    const raw = Array.isArray(subjectData?.data) ? (subjectData!.data as any[]) : [];
    return raw
      .filter((s) => s && typeof s.id === "number")
      .map((s) => ({ id: s.id as number, subject_name: String(s.subject_name ?? `Subject #${s.id}`) }));
  }, [subjectData]);
  const subjectMap = useMemo(() => {
    const m = new Map<number, string>();
    subjectList.forEach((s) => m.set(s.id, s.subject_name));
    return m;
  }, [subjectList]);
  const selectedSubjectOptions = useMemo(
    () => filterSubjects.map((id) => ({ id, subject_name: subjectMap.get(id) || `Subject #${id}` })),
    [filterSubjects, subjectMap]
  );

  const enumMap = useMemo(() => {
    const map = new Map<number, string>();
    enumList?.forEach((e) => map.set(e.id, e.enum_value));
    return map;
  }, [enumList]);

  const gradeMap = useMemo(() => {
    const map = new Map<number, string>();
    gradeList?.forEach((g) => map.set(g.id, g.grade));
    return map;
  }, [gradeList]);

  const locationMap = useMemo(() => {
    const map = new Map<number, string>();
    locationList?.forEach((l: any) => map.set(l.id, l.location_name));
    return map;
  }, [locationList]);

  const staffMap = useMemo(() => {
    const map = new Map<number, string>();
    staffList?.forEach((s) =>
      map.set(s.id, `${cap(s.teamFirstName)} ${cap(s.teamLastName)}`.trim())
    );
    return map;
  }, [staffList]);

  // The API already applied the filters, the sort and the paging — this is the page to render.
  const pageStudents: IStudent[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as IStudent[]) : []),
    [data]
  );

  // Total across all pages, straight from the paginator.
  const totalCount = useMemo(() => {
    const meta = (data as any)?.meta as IPaginationMeta | undefined;
    return typeof meta?.total === "number" ? meta.total : pageStudents.length;
  }, [data, pageStudents]);

  // Subject filter counts as one slot regardless of how many subjects
  // are selected — mirrors the badge semantics of the other filters.
  const activeFilterCount =
    [filterGrade, filterLocation, filterStatus, filterStaff].filter(Boolean).length +
    (filterSubjects.length > 0 ? 1 : 0);

  // Search effect
  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      setPage(0);
    }
  }, [debouncedSearchQuery]);

  // If the row count shrinks under us (filter applied, student deleted) the current page
  // can fall past the end of the result set — snap back rather than show an empty table.
  useEffect(() => {
    if (page > 0 && totalCount > 0 && page * rowsPerPage >= totalCount) {
      setPage(0);
    }
  }, [totalCount, page, rowsPerPage]);

  // Push list state back into the URL so refresh / back / forward /
  // shareable links preserve where the user was. `replace` (not push)
  // to avoid polluting history on every keystroke — the user gets a
  // single history entry per intentional navigation from elsewhere.
  useEffect(() => {
    const next = new URLSearchParams();
    if (page > 0) next.set("page", String(page + 1));
    if (currentSearchQuery) next.set("search", currentSearchQuery);
    if (sortValue && sortValue !== DEFAULT_SORT) next.set("sort", sortValue);
    if (filterGrade) next.set("grade_id", filterGrade);
    if (filterLocation) next.set("location_id", filterLocation);
    // Always write status — even when empty — so "Clear all" (which
    // means "show every status") survives a refresh instead of the
    // Active-default kicking back in on next mount.
    next.set("status", filterStatus);
    if (filterStaff) next.set("staff_id", filterStaff);
    if (filterSubjects.length > 0) next.set("subject_id", filterSubjects.join(","));
    setSearchParams(next, { replace: true });
    // setSearchParams is stable, safe to omit; excluding it avoids an
    // extra dependency-lint churn without changing behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, currentSearchQuery, sortValue, filterGrade, filterLocation, filterStatus, filterStaff, filterSubjects]);

  // Selection handlers
  const isAllSelected = pageStudents.length > 0 && pageStudents.every((s) => selectedIds.has(s.id));
  const isSomeSelected = pageStudents.some((s) => selectedIds.has(s.id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pageStudents.map((s) => s.id)));
    }
  };

  const handleSelectRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Delete handler
  const handleDelete = async () => {
    if (!studentToDelete) return;
    try {
      const response = await deleteStudent(String(studentToDelete.id)).unwrap();
      if (response.status) {
        showMessage("Student deleted successfully");
        setDeleteDialogOpen(false);
        setStudentToDelete(null);
      } else {
        showErrorMessage(response.message || "Failed to delete");
      }
    } catch {
      showErrorMessage("Failed to delete student");
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilterGrade("");
    setFilterLocation("");
    setFilterStatus("");
    setFilterStaff("");
    setFilterSubjects([]);
    setPage(0);
  };

  // Now that the table only holds one page, exports re-query the API with the same
  // filters and no paging so Excel/PDF still contain every matching student.
  const fetchExportRows = useCallback(async (): Promise<IStudent[]> => {
    // Exports include a Subjects column — ask the backend to attach the
    // precomputed `subjects` string[] rather than N+1 fetching client-side.
    const res = await fetchAllStudents({ ...listFilters, include_subjects: true }).unwrap();
    return Array.isArray(res?.data) ? (res.data as IStudent[]) : [];
  }, [fetchAllStudents, listFilters]);

  // Export Excel
  const handleExportExcel = async () => {
    let rows: IStudent[];
    try {
      rows = await fetchExportRows();
    } catch {
      showErrorMessage("Failed to export students");
      return;
    }
    const exportData = rows.map((row, index) => ({
      "#": index + 1,
      ID: row.reference_id || "",
      Name: fullName(row),
      Grade: gradeMap.get(row.grade_id || 0) || "",
      Country: enumMap.get(row.country_id || 0) || "",
      Phone: formatPhoneNumberForDisplay(row.parent?.contact_number) || "",
      Mode: row.study_mode || "",
      Location: locationMap.get(row.location_id || 0) || "",
      Subjects: Array.isArray(row.subjects) ? row.subjects.join(", ") : "",
      Status: row.status || "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, `Students_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  // Export PDF
  const handleExportPDF = async () => {
    let rows: IStudent[];
    try {
      rows = await fetchExportRows();
    } catch {
      showErrorMessage("Failed to export students");
      return;
    }
    const doc = new jsPDF("l", "mm", "a4");
    const tableData = rows.map((row, i) => [
      i + 1,
      row.reference_id || "",
      fullName(row),
      gradeMap.get(row.grade_id || 0) || "",
      enumMap.get(row.country_id || 0) || "",
      formatPhoneNumberForDisplay(row.parent?.contact_number) || "",
      row.study_mode || "",
      Array.isArray(row.subjects) ? row.subjects.join(", ") : "",
      row.status || "",
    ]);
    const headers = [["#", "ID", "Name", "Grade", "Country", "Phone", "Mode", "Subjects", "Status"]];

    if (companyLogo) {
      const img = new Image();
      img.src = companyLogo;
      const generatePdf = (includeImg: boolean) => {
        if (includeImg) doc.addImage(img, "PNG", 15, 10, logowidthPrint, 10);
        (doc as any).autoTable({
          head: headers,
          body: tableData,
          startY: 25,
          theme: "grid",
          styles: { fontSize: 8 },
          headStyles: { fillColor: [99, 102, 241] },
        });
        doc.save(`Students_${dayjs().format("YYYY-MM-DD")}.pdf`);
      };
      img.onload = () => generatePdf(true);
      img.onerror = () => generatePdf(false);
    } else {
      (doc as any).autoTable({
        head: headers,
        body: tableData,
        startY: 15,
        theme: "grid",
        styles: { fontSize: 8 },
        headStyles: { fillColor: [99, 102, 241] },
      });
      doc.save(`Students_${dayjs().format("YYYY-MM-DD")}.pdf`);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  // Loading state
  if (isLoading || enumLoading || gradeLoading) {
    return <LoadingPage open={true} />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: "#F7F9FC", minHeight: "100%" }}>
      {/* ── PAGE HEADER ────────────────────────────────────────────
             Mirrors the Courses List hero — 48 px branded icon tile
             + title + count sub-line + polished secondary/primary
             action buttons. Softer surface + subtle blue-tinted
             shadow so it reads as an intentional page anchor rather
             than a plain row. */}
      <Box
        sx={{
          ...surfaceSx,
          p: { xs: 2, md: 2.5 },
          mb: 2,
          borderRadius: 3,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(31,111,235,0.04)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: tint12(B13.primary),
                color: B13.primary,
                "& svg": { fontSize: 26 },
                flexShrink: 0,
              }}
            >
              <GroupIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
                Students
              </Typography>
              <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mt: 0.25 }}>
                {totalCount.toLocaleString()} student{totalCount !== 1 ? "s" : ""} total
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon sx={{ fontSize: "16px !important" }} />}
              onClick={handleExportExcel}
              sx={{
                ...secondaryBtnSx,
                minHeight: 44,
                borderRadius: "10px",
                px: 2,
              }}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon sx={{ fontSize: "16px !important" }} />}
              onClick={handleExportPDF}
              sx={{
                ...secondaryBtnSx,
                minHeight: 44,
                borderRadius: "10px",
                px: 2,
              }}
            >
              PDF
            </Button>
            {canCreate("Student") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/student/create")}
                sx={{
                  ...primaryBtnSx,
                  minHeight: 44,
                  borderRadius: "10px",
                  px: 2.25,
                  boxShadow: "0 4px 10px rgba(31,111,235,0.24)",
                  "&:hover": {
                    bgcolor: B13.primary,
                    filter: "brightness(0.95)",
                    boxShadow: "0 6px 14px rgba(31,111,235,0.30)",
                  },
                }}
              >
                Add Student
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── SEARCH + FILTER TOOLBAR ────────────────────────────────
             Wrapped inside a proper surface card (matches Courses
             List). Search keeps the widest slot; Filters + Sort are
             right-aligned. When rows are selected the bulk action
             bar folds in below the search on tablet/mobile so it
             doesn't push the sort control off-screen. */}
      <Box
        sx={{
          ...surfaceSx,
          p: { xs: 1.75, md: 2 },
          mb: 2,
          borderRadius: 3,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, ID, or phone..."
            size="small"
            sx={{
              ...filterFieldSx,
              flex: 1,
              minWidth: 240,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: B13Ink.ink3 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearchQuery("")}
                    sx={{
                      color: B13Ink.ink3,
                      "&:hover": { color: B13.primary, bgcolor: tint12(B13.primary) },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button
            variant={showFilters ? "contained" : "outlined"}
            startIcon={<FilterListIcon sx={{ fontSize: "16px !important" }} />}
            onClick={() => setShowFilters(!showFilters)}
            sx={
              showFilters
                ? {
                    ...primaryBtnSx,
                    minHeight: 44,
                    borderRadius: "10px",
                    px: 2,
                    boxShadow: "0 4px 10px rgba(31,111,235,0.24)",
                    "&:hover": {
                      bgcolor: B13.primary,
                      filter: "brightness(0.95)",
                    },
                  }
                : {
                    ...secondaryBtnSx,
                    minHeight: 44,
                    borderRadius: "10px",
                    px: 2,
                  }
            }
          >
            Filters
            {activeFilterCount > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  px: 0.75,
                  minWidth: 20,
                  height: 20,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "10px",
                  fontSize: 11,
                  fontWeight: 800,
                  lineHeight: 1,
                  bgcolor: showFilters ? "rgba(255,255,255,0.22)" : tint12(B13.primary),
                  color: showFilters ? "#FFFFFF" : B13.primary,
                }}
              >
                {activeFilterCount}
              </Box>
            )}
          </Button>

          {/* Sort */}
          <TextField
            select
            value={sortValue}
            onChange={(e) => {
              setSortValue(e.target.value);
              setPage(0);
            }}
            sx={{ ...filterFieldSx, minWidth: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SwapVertIcon sx={{ fontSize: 18, color: B13Ink.ink3 }} />
                </InputAdornment>
              ),
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Bulk-actions bar — folds under the toolbar on tablet/
            mobile widths, sits inline on the row when there is space.
            Preserves every existing handler (Message / Assign Class /
            clear selection). */}
        {selectedIds.size > 0 && (
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: tint12(B13.primary),
              borderRadius: "10px",
              px: 1.5,
              py: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: B13.primary }}>
              {selectedIds.size} selected
            </Typography>
            <Button
              size="small"
              startIcon={<SendIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => setBulkMessageOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: B13.primary,
                borderRadius: "8px",
                px: 1.25,
                "&:hover": { bgcolor: "rgba(255,255,255,0.6)" },
              }}
            >
              Message
            </Button>
            <Button
              size="small"
              startIcon={<SchoolIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => setBulkAssignOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: B13.primary,
                borderRadius: "8px",
                px: 1.25,
                "&:hover": { bgcolor: "rgba(255,255,255,0.6)" },
              }}
            >
              Assign Class
            </Button>
            <IconButton
              size="small"
              onClick={() => setSelectedIds(new Set())}
              aria-label="Clear selection"
              sx={{
                ml: "auto",
                color: B13.primary,
                "&:hover": { bgcolor: "rgba(255,255,255,0.6)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* ── COLLAPSIBLE FILTER PANEL ───────────────────────────────
             Reveals the four refinement dropdowns (Grade / Location /
             Status / Staff). Uses the same premium field styling as
             the search/sort toolbar so every control on the page
             reads as one control family. */}
      <Collapse in={showFilters}>
        <Box
          sx={{
            ...surfaceSx,
            mb: 2,
            p: { xs: 1.75, md: 2 },
            borderRadius: 3,
            boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              alignItems: "center",
            }}
          >
            <TextField
              select
              label="Grade"
              value={filterGrade}
              onChange={(e) => { setFilterGrade(e.target.value); setPage(0); }}
              sx={filterFieldSx}
            >
              <MenuItem value="">All Grades</MenuItem>
              {gradeList.map((g) => (
                <MenuItem key={g.id} value={String(g.id)}>{g.grade}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Location"
              value={filterLocation}
              onChange={(e) => { setFilterLocation(e.target.value); setPage(0); }}
              sx={filterFieldSx}
            >
              <MenuItem value="">All Locations</MenuItem>
              {locationList.map((l: any) => (
                <MenuItem key={l.id} value={String(l.id)}>{l.location_name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
              sx={filterFieldSx}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              {/* Value stays "drop" (matches existing DB rows). The
                  studentsController::index filter folds legacy "Dropped"
                  rows in server-side. Label reads "Dropped" for
                  consistency with the lifecycle model. */}
              <MenuItem value="drop">Dropped</MenuItem>
              <MenuItem value="on hold">On Hold</MenuItem>
            </TextField>
            <SearchableSelect
              value={filterStaff}
              onChange={(v) => { setFilterStaff(v); setPage(0); }}
              options={staffList.map((s: any) => ({
                value: String(s.id),
                label: `${cap(s.teamFirstName)} ${cap(s.teamLastName)}`.trim() || `Staff #${s.id}`,
              }))}
              label="Staff"
              allLabel="All Staff"
              searchPlaceholder="Search staff…"
              noResultsText="No staff found"
              ariaLabel="Filter by staff"
              sx={filterFieldSx}
            />
            {/* Subject multi-select. Uses MUI Autocomplete so the field
                is searchable and renders selected items as chips inline.
                When ≥ 3 subjects are selected we collapse the display to
                a "N Subjects" tag so the field never overflows. OR
                semantics server-side: a student matches if enrolled in
                ANY selected subject. */}
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              options={subjectList}
              value={selectedSubjectOptions}
              getOptionLabel={(opt) => opt.subject_name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, next) => {
                setFilterSubjects(next.map((o) => o.id));
                setPage(0);
              }}
              noOptionsText="No subjects found"
              renderTags={(value, getTagProps) => {
                if (value.length >= 3) {
                  return [
                    <Chip
                      key="count"
                      size="small"
                      label={`${value.length} Subjects`}
                      sx={{ bgcolor: tint12(B13.primary), color: B13.primary, fontWeight: 700 }}
                    />,
                  ];
                }
                return value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.subject_name}
                    {...getTagProps({ index })}
                    key={option.id}
                    sx={{ bgcolor: tint12(B13.primary), color: B13.primary, fontWeight: 700 }}
                  />
                ));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subject"
                  placeholder={filterSubjects.length === 0 ? "All Subjects" : ""}
                  sx={filterFieldSx}
                />
              )}
            />
          </Box>
          {activeFilterCount > 0 && (
            <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                startIcon={<CloseIcon sx={{ fontSize: "16px !important" }} />}
                onClick={clearFilters}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12.5,
                  color: B13.primary,
                  borderRadius: "8px",
                  px: 1.25,
                  "&:hover": { bgcolor: tint12(B13.primary) },
                }}
              >
                Clear all
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Active filter chips — surfaced when the panel is closed so
          the user can still see + remove active refinements at a
          glance. Removal preserves the exact deleters previously
          wired (no filter-clear side effects lost). */}
      {activeFilterCount > 0 && !showFilters && (
        <Box sx={{ display: "flex", gap: 0.75, mb: 2, flexWrap: "wrap" }}>
          {filterGrade && (
            <Chip
              label={`Grade: ${gradeMap.get(Number(filterGrade)) || filterGrade}`}
              size="small"
              onDelete={() => { setFilterGrade(""); setPage(0); }}
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: tint12(B13.primary),
                color: B13.primary,
                border: `1px solid ${tint12(B13.primary)}`,
                "& .MuiChip-deleteIcon": {
                  color: B13.primary,
                  "&:hover": { color: B13.primary, opacity: 0.7 },
                },
              }}
            />
          )}
          {filterLocation && (
            <Chip
              label={`Location: ${locationMap.get(Number(filterLocation)) || filterLocation}`}
              size="small"
              onDelete={() => { setFilterLocation(""); setPage(0); }}
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: tint12(B13.primary),
                color: B13.primary,
                border: `1px solid ${tint12(B13.primary)}`,
                "& .MuiChip-deleteIcon": {
                  color: B13.primary,
                  "&:hover": { color: B13.primary, opacity: 0.7 },
                },
              }}
            />
          )}
          {filterStatus && (
            <Chip
              label={`Status: ${displayStatus(filterStatus)}`}
              size="small"
              onDelete={() => { setFilterStatus(""); setPage(0); }}
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: tint12(B13.primary),
                color: B13.primary,
                border: `1px solid ${tint12(B13.primary)}`,
                "& .MuiChip-deleteIcon": {
                  color: B13.primary,
                  "&:hover": { color: B13.primary, opacity: 0.7 },
                },
              }}
            />
          )}
          {filterStaff && (
            <Chip
              label={`Staff: ${staffMap.get(Number(filterStaff)) || filterStaff}`}
              size="small"
              onDelete={() => { setFilterStaff(""); setPage(0); }}
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: tint12(B13.primary),
                color: B13.primary,
                border: `1px solid ${tint12(B13.primary)}`,
                "& .MuiChip-deleteIcon": {
                  color: B13.primary,
                  "&:hover": { color: B13.primary, opacity: 0.7 },
                },
              }}
            />
          )}
          {filterSubjects.length > 0 && (
            <Chip
              label={
                filterSubjects.length <= 2
                  ? `Subjects: ${filterSubjects.map((id) => subjectMap.get(id) || `#${id}`).join(", ")}`
                  : `Subjects: ${filterSubjects.length} selected`
              }
              size="small"
              onDelete={() => { setFilterSubjects([]); setPage(0); }}
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: tint12(B13.primary),
                color: B13.primary,
                border: `1px solid ${tint12(B13.primary)}`,
                "& .MuiChip-deleteIcon": {
                  color: B13.primary,
                  "&:hover": { color: B13.primary, opacity: 0.7 },
                },
              }}
            />
          )}
        </Box>
      )}

      {/* ── TABLE CARD ─────────────────────────────────────────────
             One premium container wraps the whole data grid + its
             pagination footer. Uppercase eyebrow labels in the header
             row + a soft #F7F9FC background + a subtle divider keep
             the table readable at 25 rows-per-page density without
             feeling like a raw spreadsheet. */}
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: 3,
          border: `1px solid ${B13.line}`,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03), 0 6px 16px rgba(15,23,42,0.04)",
          overflow: "hidden",
        }}
      >
        {isFetching && <LoadingPage open={true} />}
        <TableContainer sx={{ display: isFetching ? "none" : "block" }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: "#F7F9FC",
                  "& .MuiTableCell-head": {
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: B13Ink.ink3,
                    py: 1.5,
                    borderBottom: `1px solid ${B13.line}`,
                  },
                }}
              >
                <TableCell padding="checkbox" sx={{ pl: 2 }}>
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isSomeSelected && !isAllSelected}
                    onChange={handleSelectAll}
                    sx={{
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                      color: B13Ink.ink3,
                      "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: B13.primary },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: 50 }}>#</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Student</TableCell>
                {!isMobileRes && (
                  <>
                    <TableCell>Grade</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Location</TableCell>
                  </>
                )}
                <TableCell>Status</TableCell>
                <TableCell sx={{ width: 160 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError || pageStudents.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={isMobileRes ? 5 : 10}
                    align="center"
                    sx={{ py: { xs: 6, md: 8 }, borderBottom: "none" }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2.5,
                        mx: "auto",
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: tint12(B13.primary),
                        color: B13.primary,
                        "& svg": { fontSize: 28 },
                      }}
                    >
                      <GroupIcon />
                    </Box>
                    <Typography sx={{ ...B13TypeScale.bodyStrong, color: B13.ink }}>
                      {isError ? "Error loading students" : "No students found"}
                    </Typography>
                    <Typography
                      sx={{
                        ...B13TypeScale.body,
                        color: B13Ink.ink2,
                        mt: 0.5,
                      }}
                    >
                      {isError
                        ? "Please try again later"
                        : searchQuery || activeFilterCount > 0
                        ? "Try adjusting your search or filters."
                        : "Add your first student to get started."}
                    </Typography>
                    {!isError && (searchQuery || activeFilterCount > 0) && (
                      <Button
                        variant="outlined"
                        onClick={clearFilters}
                        sx={{
                          ...secondaryBtnSx,
                          mt: 2,
                          minHeight: 40,
                          borderRadius: "10px",
                          px: 2,
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                pageStudents.map((row, index) => {
                  const isSelected = selectedIds.has(row.id);
                  const statusColors = getStatusColor(row.status);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={isSelected}
                      sx={{
                        cursor: "pointer",
                        transition: "background-color 120ms ease",
                        "& .MuiTableCell-root": {
                          borderBottom: `1px solid ${B13.line}`,
                          py: 1.5,
                        },
                        "&:hover": {
                          bgcolor: "#F8FBFF",
                        },
                        "&.Mui-selected": {
                          bgcolor: "#F3F8FF !important",
                        },
                        "&.Mui-selected:hover": {
                          bgcolor: "#EAF2FF !important",
                        },
                      }}
                    >
                      {/* Checkbox */}
                      <TableCell padding="checkbox" sx={{ pl: 2 }}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => handleSelectRow(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: B13Ink.ink3,
                            "&.Mui-checked": { color: B13.primary },
                          }}
                        />
                      </TableCell>

                      {/* # */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: B13Ink.ink3,
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>

                      {/* Student Name + Avatar */}
                      <TableCell
                        onClick={() => navigate(`/student-history/${row.id}`)}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <StudentAvatar
                            student={row}
                            fallbackLabel={fullName(row)}
                            size={36}
                            imgProps={{
                              onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
                                e.currentTarget.src = ReplaceImage;
                              },
                            }}
                          />
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                              <Typography
                                sx={{
                                  fontSize: 13.75,
                                  fontWeight: 700,
                                  color: B13.ink,
                                  lineHeight: 1.3,
                                  letterSpacing: "-0.005em",
                                  transition: "color 120ms ease",
                                  "&:hover": { color: B13.primary },
                                }}
                              >
                                {fullName(row)}
                              </Typography>
                              {/* Payment-due red flag — driven by
                                  server-computed `payment_due`
                                  (outstanding invoice balance > 0).
                                  Rendered only when true. */}
                              {hasPaymentDue(row as any) && (
                                <Tooltip title={((row as any).outstanding_balance ?? 0) > 0
                                  ? `Outstanding balance: $${Number((row as any).outstanding_balance).toFixed(2)}`
                                  : "Outstanding balance"}>
                                  <Chip
                                    icon={<FlagOutlinedIcon sx={{ fontSize: "12px !important" }} />}
                                    label="Payment Due"
                                    size="small"
                                    aria-label="Payment due"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      height: 20,
                                      fontSize: 10.5,
                                      fontWeight: 800,
                                      letterSpacing: "0.02em",
                                      bgcolor: "#FEE2E2",
                                      color: "#B91C1C",
                                      border: "1px solid #FCA5A5",
                                      "& .MuiChip-icon": { color: "#B91C1C", ml: 0.5 },
                                      "& .MuiChip-label": { px: 0.75 },
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {/* Health-alert indicator — server-side enrichment only
                                  surfaces health_alert when the caller holds
                                  StudentNote_View, so no client-side permission
                                  re-check is needed here. Silent icon, tooltip,
                                  click bubbles up so the row still opens Profile. */}
                              {(row as any)?.health_alert?.has_alert && (
                                <Tooltip title={
                                  ((row as any).health_alert.count || 0) > 1
                                    ? `Health alert on file · ${(row as any).health_alert.count} notes`
                                    : "Health alert on file"
                                }>
                                  <MedicalServicesOutlinedIcon
                                    sx={{ fontSize: 15, color: "#b45309" }}
                                    aria-label="Health alert on file"
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            {row.reference_id && (
                              <Typography sx={{ fontSize: 11.5, color: B13Ink.ink3, mt: 0.2, fontWeight: 600 }}>
                                {row.reference_id}
                              </Typography>
                            )}
                            {/* Grade shown inline on mobile (Grade column is desktop-only) */}
                            {isMobileRes && row.grade_id && gradeMap.get(row.grade_id) && (
                              <Chip
                                label={gradeMap.get(row.grade_id)}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  mt: 0.3,
                                  bgcolor: tint12(B13.primary),
                                  color: B13.primary,
                                  border: "none",
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Desktop columns */}
                      {!isMobileRes && (
                        <>
                          {/* Grade — compact tinted pill so it reads at
                              a glance even in a dense row. Falls back
                              to a muted dash for students without a
                              grade assigned. */}
                          <TableCell>
                            {row.grade_id && gradeMap.get(row.grade_id) ? (
                              <Chip
                                label={gradeMap.get(row.grade_id)}
                                size="small"
                                sx={{
                                  height: 24,
                                  minWidth: 30,
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  bgcolor: tint12(B13.primary),
                                  color: B13.primary,
                                  border: "none",
                                  borderRadius: "8px",
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: 13, color: B13Ink.ink3 }}>—</Typography>
                            )}
                          </TableCell>

                          {/* Country */}
                          <TableCell>
                            <Typography sx={{ fontSize: 13, color: B13Ink.ink2 }}>
                              {enumMap.get(row.country_id || 0) || (
                                <Box component="span" sx={{ color: B13Ink.ink3 }}>—</Box>
                              )}
                            </Typography>
                          </TableCell>

                          {/* Phone */}
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: 13,
                                color: B13Ink.ink2,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formatPhoneNumberForDisplay(row.parent?.contact_number) || (
                                <Box component="span" sx={{ color: B13Ink.ink3 }}>—</Box>
                              )}
                            </Typography>
                          </TableCell>

                          {/* Mode — Online / In Person (softened tint
                              pair matches the rest of the palette;
                              raw underscore values from the API are
                              formatted "In Person" for display but
                              the backend value is untouched). */}
                          <TableCell>
                            {row.study_mode ? (() => {
                              const raw = String(row.study_mode);
                              const isOnline = raw.toLowerCase() === "online";
                              const label = raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                              return (
                                <Chip
                                  label={label}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    borderRadius: "8px",
                                    bgcolor: isOnline ? "#F5F3FF" : "#EFF6FF",
                                    color: isOnline ? "#7C3AED" : "#2563EB",
                                    border: "none",
                                    "& .MuiChip-label": { px: 1 },
                                  }}
                                />
                              );
                            })() : (
                              <Typography sx={{ fontSize: 13, color: B13Ink.ink3 }}>—</Typography>
                            )}
                          </TableCell>

                          {/* Location */}
                          <TableCell>
                            <Typography sx={{ fontSize: 13, color: B13Ink.ink2 }}>
                              {/* location_id is a campus (location_tables) FK — resolving it against
                                  enum_tables printed an unrelated city enum that contradicted the filter. */}
                              {locationMap.get(row.location_id || 0) || (
                                <Box component="span" sx={{ color: B13Ink.ink3 }}>—</Box>
                              )}
                            </Typography>
                          </TableCell>
                        </>
                      )}

                      {/* Status — same semantic colours the existing
                          getStatusColor() helper returns, wrapped in a
                          consistent pill treatment (24 px, 8 px
                          radius, subtle top-border tint so the chip
                          reads as a badge rather than a floating
                          blob). */}
                      <TableCell>
                        <Chip
                          label={displayStatus(row.status)}
                          size="small"
                          aria-label={`Status ${displayStatus(row.status)}`}
                          sx={{
                            height: 24,
                            fontSize: 11.5,
                            fontWeight: 800,
                            bgcolor: statusColors.bg,
                            color: statusColors.text,
                            border: `1px solid ${alpha(statusColors.text, 0.16)}`,
                            borderRadius: "8px",
                            letterSpacing: "0.02em",
                            "& .MuiChip-label": { px: 1.25 },
                          }}
                        />
                      </TableCell>

                      {/* Row Actions — 32 px icon buttons with proper
                          hover surfaces + tooltips. View / Edit /
                          Message / More retain their exact handlers
                          and permission gating. */}
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 0.35,
                          }}
                        >
                          <Tooltip title="View student">
                            <IconButton
                              size="small"
                              aria-label={`View ${fullName(row)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/student-history/${row.id}`);
                              }}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                color: B13Ink.ink3,
                                transition: "background-color 140ms ease, color 140ms ease",
                                "&:hover": {
                                  bgcolor: tint12(B13.primary),
                                  color: B13.primary,
                                },
                              }}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {canEdit("Student") && (
                            <Tooltip title="Edit student">
                              <IconButton
                                size="small"
                                aria-label={`Edit ${fullName(row)}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/student-history/${row.id}?tab=Profile`);
                                }}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "8px",
                                  color: B13Ink.ink3,
                                  transition: "background-color 140ms ease, color 140ms ease",
                                  "&:hover": {
                                    bgcolor: tint12(B13.primary),
                                    color: B13.primary,
                                  },
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Message">
                            <IconButton
                              size="small"
                              aria-label={`Message ${fullName(row)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/message/create`);
                              }}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                color: B13Ink.ink3,
                                transition: "background-color 140ms ease, color 140ms ease",
                                "&:hover": {
                                  bgcolor: tint12(B13.primary),
                                  color: B13.primary,
                                },
                              }}
                            >
                              <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More actions">
                            <IconButton
                              size="small"
                              aria-label={`More actions for ${fullName(row)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRowMenuAnchor(e.currentTarget);
                                setRowMenuStudent(row);
                              }}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                color: B13Ink.ink3,
                                transition: "background-color 140ms ease, color 140ms ease",
                                "&:hover": {
                                  bgcolor: tint12(B13.primary),
                                  color: B13.primary,
                                },
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination footer ─────────────────────────────────────
             Stays visible during isFetching so the Next/Prev controls
             don't vanish mid-navigation (the table loader above
             handles the visual "loading" cue). Softer top-border +
             a slightly stronger count line so the footer reads as
             the natural bottom of the card. */}
        {totalCount > rowsPerPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2.25,
              py: 1.75,
              borderTop: `1px solid ${B13.line}`,
              bgcolor: "#FFFFFF",
              opacity: isFetching ? 0.6 : 1,
              transition: "opacity 0.15s",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: B13Ink.ink2, fontWeight: 600 }}>
              Showing{" "}
              <Box component="span" sx={{ color: B13.ink, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                {(page * rowsPerPage + 1).toLocaleString()}
              </Box>{" "}
              to{" "}
              <Box component="span" sx={{ color: B13.ink, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                {Math.min((page + 1) * rowsPerPage, totalCount).toLocaleString()}
              </Box>{" "}
              of{" "}
              <Box component="span" sx={{ color: B13.ink, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                {totalCount.toLocaleString()}
              </Box>{" "}
              students
            </Typography>
            <Pagination
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
            />
          </Box>
        )}
      </Box>

      {/* Row three-dot menu */}
      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={() => setRowMenuAnchor(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            minWidth: 160,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            "& .MuiMenuItem-root": { fontSize: 13, py: 1 },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (rowMenuStudent) navigate(`/student-history/${rowMenuStudent.id}`);
            setRowMenuAnchor(null);
          }}
        >
          <ListItemIcon><VisibilityOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText>View Profile</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenuStudent) navigate(`/student-history/${rowMenuStudent.id}?tab=Profile`);
            setRowMenuAnchor(null);
          }}
        >
          <ListItemIcon><EditOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/message/create");
            setRowMenuAnchor(null);
          }}
        >
          <ListItemIcon><EmailOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText>Send Message</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setStudentToDelete(rowMenuStudent);
            setDeleteDialogOpen(true);
            setRowMenuAnchor(null);
          }}
          sx={{ color: theme.palette.error.main }}
        >
          <ListItemIcon><CloseIcon sx={{ fontSize: 18, color: "error.main" }} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation */}
      <DeletePopup
        title={`Delete ${studentToDelete ? fullName(studentToDelete) : "student"}?`}
        content="This action cannot be undone. The student record will be permanently removed."
        open={deleteDialogOpen}
        handleCloseDelete={() => setDeleteDialogOpen(false)}
        onConfirm={() => handleDelete()}
      />

      {/* Bulk Message Dialog */}
      <Dialog
        open={bulkMessageOpen}
        onClose={() => setBulkMessageOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Send Message to {selectedIds.size} Student{selectedIds.size !== 1 ? "s" : ""}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            This will send a message to the parents/guardians of the selected students.
          </Typography>
          <TextField
            label="Subject"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Message"
            fullWidth
            multiline
            rows={4}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkMessageOpen(false)} sx={{ color: "text.secondary" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => {
              showMessage(`Message sent to ${selectedIds.size} student(s)`);
              setBulkMessageOpen(false);
              setSelectedIds(new Set());
            }}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Assign Class Dialog */}
      <Dialog
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Assign Class to {selectedIds.size} Student{selectedIds.size !== 1 ? "s" : ""}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            Select a class to assign to all selected students.
          </Typography>
          <TextField select label="Grade" fullWidth size="small" sx={{ mb: 2 }}>
            {gradeList.map((g) => (
              <MenuItem key={g.id} value={String(g.id)}>{g.grade}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Subject" fullWidth size="small" sx={{ mb: 2 }}>
            <MenuItem value="">Select subject</MenuItem>
          </TextField>
          <TextField select label="Staff" fullWidth size="small">
            {staffList.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {cap(s.teamFirstName)} {cap(s.teamLastName)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignOpen(false)} sx={{ color: "text.secondary" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SchoolIcon />}
            onClick={() => {
              showMessage(`Class assigned to ${selectedIds.size} student(s)`);
              setBulkAssignOpen(false);
              setSelectedIds(new Set());
            }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Index;
